import { useState } from 'react';
import { useQuery } from 'react-query';

import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { Checkbox } from 'modules/v2/common/components/UIComponents';
import {
  getModOrderRecipients,
  setModOrderRecipientsSelection,
  updateModOrderRecipient,
} from 'modules/api/digitalServices/mailOnDemand';
import { cn } from 'modules/v2/common/utils/cn';

import CreditsBalanceWidget from '../../components/CreditsBalanceWidget';
import EditAddressModal from '../components/EditAddressModal';
import strings, { formatMessage } from '../../strings';
import { formatCredits } from '../../utils';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const recipientName = (r) =>
  `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—';

const recipientAddress = (r) => {
  const line = [r.address1, r.city, r.state, r.zip].filter(Boolean).join(', ');
  return line || null;
};

const ReviewStep = ({ order, onOrderChange, onContinue }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [mutating, setMutating] = useState(false);
  const [editError, setEditError] = useState(false);

  const orderId = order?._id;
  const resolution = order?.resolution || {};
  const pricing = order?.pricing || {};

  const selectedCount =
    resolution.selectedMailableCount ?? resolution.mailableCount ?? 0;
  const belowMinimum =
    pricing.minQuantity !== undefined && selectedCount < pricing.minQuantity;

  const { data: recipientsPage, isLoading, isError, refetch } = useQuery(
    ['modRecipients', orderId, page, pageSize, search],
    () => getModOrderRecipients(orderId, { page, limit: pageSize, search }),
    { keepPreviousData: true, enabled: Boolean(orderId) },
  );

  // Changing the page size always re-queries from page 1; the page indicator +
  // Prev/Next then paginate the rest at the chosen size (task 47).
  const changePageSize = (event) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const rows = recipientsPage?.data || [];
  const meta = recipientsPage?.meta || {};

  const applyServerResult = (result) => {
    if (result?.order) {
      onOrderChange(result.order);
    }
    refetch();
  };

  const toggleOne = async (recipient, nextSelected) => {
    setMutating(true);
    try {
      const result = await updateModOrderRecipient(orderId, recipient._id, {
        selected: nextSelected,
      });
      applyServerResult(result);
    } catch (error) {
      refetch();
    } finally {
      setMutating(false);
    }
  };

  const bulkSelect = async (nextSelected) => {
    setMutating(true);
    try {
      const result = await setModOrderRecipientsSelection(orderId, nextSelected);
      applyServerResult(result);
    } catch (error) {
      // Re-sync from the server so the table never shows an un-applied bulk
      // change (symmetric with toggleOne).
      refetch();
    } finally {
      setMutating(false);
    }
  };

  const saveAddress = async (fields) => {
    setMutating(true);
    setEditError(false);
    try {
      const result = await updateModOrderRecipient(orderId, editing._id, fields);
      applyServerResult(result);
      setEditing(null);
    } catch (error) {
      setEditError(true);
    } finally {
      setMutating(false);
    }
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const summaryCards = [
    {
      label: strings.review.selectedLabel,
      testId: 'mod-selected-count',
      value: (
        <>
          {formatCredits(selectedCount)}{' '}
          <span className="text-sm font-medium text-neutral-400">
            {formatMessage(strings.review.selectedOfTotal, {
              selected: selectedCount,
              total: resolution.mailableCount || 0,
            })}
          </span>
        </>
      ),
    },
    {
      label: strings.review.skippedLabel,
      testId: 'mod-skipped-count',
      value: formatCredits(resolution.skippedCount),
    },
    {
      label: strings.review.costLabel,
      testId: 'mod-credits-required',
      value: formatCredits(pricing.creditsRequired),
      hint: formatMessage(strings.review.costPerRecipient, {
        creditsPerRecipient: pricing.creditsPerRecipient,
      }),
    },
  ];

  const renderRecipients = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center gap-3 py-12">
          <Spinner />
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex flex-col items-start gap-3 px-4 py-6">
          <p>{strings.review.recipientsLoadError}</p>
          <Button color="outlined" onClick={() => refetch()}>
            {strings.common.retry}
          </Button>
        </div>
      );
    }
    if (rows.length === 0) {
      return (
        <p className="py-8 text-center text-neutral-500">
          {strings.review.recipientsEmpty}
        </p>
      );
    }
    return (
      <table
        className="w-full text-left text-sm"
        data-testid="mod-recipients-table"
      >
        <tbody>
          {rows.map((recipient) => {
            const address = recipientAddress(recipient);
            const isSelected = recipient.selected !== false;
            return (
              <tr
                key={recipient._id}
                className={cn(
                  'border-b border-neutral-100 last:border-0',
                  !recipient.mailable || !isSelected ? 'opacity-60' : '',
                )}
              >
                <td className="w-8 py-2.5 pl-4">
                  {recipient.mailable && (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleOne(recipient, !isSelected)}
                      disabled={mutating}
                      aria-label={recipientName(recipient)}
                      data-testid={`mod-recipient-toggle-${recipient._id}`}
                    />
                  )}
                </td>
                <td className="py-2.5 font-medium">
                  {recipientName(recipient)}
                  {recipient.overriddenAt && (
                    <span className="ml-2 rounded-full bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                      {strings.review.corrected}
                    </span>
                  )}
                </td>
                <td className="py-2.5 text-neutral-500">
                  {address || (
                    <span className="italic text-neutral-400">
                      {strings.review.noAddress}
                    </span>
                  )}
                </td>
                <td className="py-2.5">
                  {recipient.mailable ? (
                    <span className="rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                      {strings.review.statusMailable}
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                      {strings.review.statusSkipped}
                    </span>
                  )}
                </td>
                <td className="py-2.5 pr-4 text-right">
                  <button
                    type="button"
                    className={cn(
                      'text-xs font-semibold text-primary-700',
                      !recipient.mailable &&
                        'rounded-md border border-primary-500 px-2 py-1',
                    )}
                    onClick={() => {
                      setEditError(false);
                      setEditing(recipient);
                    }}
                    data-testid={`mod-recipient-edit-${recipient._id}`}
                  >
                    {recipient.mailable
                      ? strings.review.edit
                      : strings.review.addAddress}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div>
      <h2 className="mb-6 text-lg font-semibold">{strings.review.title}</h2>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-lg border border-neutral-200 bg-white p-4"
          >
            <div className="text-sm text-neutral-500">{card.label}</div>
            <div className="text-2xl font-semibold" data-testid={card.testId}>
              {card.value}
            </div>
            {card.hint && (
              <div className="text-[11px] text-neutral-400">{card.hint}</div>
            )}
          </div>
        ))}
        <CreditsBalanceWidget showBuyButton />
      </div>

      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-semibold">{strings.review.recipientsTitle}</span>
            <button
              type="button"
              className="text-xs font-semibold text-primary-700 disabled:opacity-50"
              onClick={() => bulkSelect(true)}
              disabled={mutating}
              data-testid="mod-select-all"
            >
              {formatMessage(strings.review.selectAll, {
                count: resolution.mailableCount || 0,
              })}
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-neutral-500 disabled:opacity-50"
              onClick={() => bulkSelect(false)}
              disabled={mutating}
              data-testid="mod-select-none"
            >
              {strings.review.selectNone}
            </button>
          </div>
          <form onSubmit={submitSearch}>
            <input
              className="w-64 rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
              placeholder={strings.review.searchPlaceholder}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              data-testid="mod-recipients-search"
            />
          </form>
        </div>

        {renderRecipients()}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
          <label className="flex items-center gap-2">
            <span>{strings.review.perPageLabel}</span>
            <select
              className="rounded-md border border-neutral-300 px-2 py-1 text-xs"
              value={pageSize}
              onChange={changePageSize}
              data-testid="mod-recipients-pagesize"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          {(meta.lastPage || 1) > 1 && (
            <div className="flex items-center gap-2">
              <Button
                color="outlined"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                {strings.credits.previousPage}
              </Button>
              <span>
                {formatMessage(strings.credits.pageIndicator, {
                  currentPage: page,
                  lastPage: meta.lastPage || 1,
                })}
              </span>
              <Button
                color="outlined"
                size="sm"
                disabled={page >= (meta.lastPage || 1)}
                onClick={() => setPage((current) => current + 1)}
              >
                {strings.credits.nextPage}
              </Button>
            </div>
          )}
        </div>
      </div>

      {belowMinimum && (
        <div
          className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm"
          data-testid="mod-min-quantity-warning"
        >
          {formatMessage(strings.review.minQuantityNotMet, {
            minQuantity: pricing.minQuantity,
            selectedCount,
          })}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button
          onClick={onContinue}
          disabled={belowMinimum || mutating}
          data-testid="mod-review-continue"
        >
          {formatMessage(strings.review.continueToPayWithCount, {
            count: selectedCount,
          })}
        </Button>
      </div>

      {editing && (
        <EditAddressModal
          recipient={editing}
          onSave={saveAddress}
          onClose={() => setEditing(null)}
          isSaving={mutating}
          error={editError}
        />
      )}
    </div>
  );
};

export default ReviewStep;
